import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  boardRecentViewSchema,
  boardStarStateSchema,
  reorderBoardStarBodySchema,
  type ApiError,
  type Board,
  type BoardPageBoard,
  type BoardRecentView,
  type BoardStarState,
  type BoardsPage,
  type ReorderBoardStarBody,
  type SearchJumpTo,
} from '@molt/shared';
import { del, get, patch, put } from './api';
import { announce } from './announce';
import { useInterfaceLanguage } from './interface-language';
import { queryKeys, type MoltQueryKey } from './query-keys';

const BOARDS_PAGE_PREFIX: MoltQueryKey = ['boards', 'page'];

interface ReorderStarVariables {
  boardId: string;
  previousBoardId: string | null;
  nextBoardId: string | null;
}

interface ReorderStarContext {
  boardPages: [MoltQueryKey, BoardsPage | undefined][];
  jumpTo: SearchJumpTo | undefined;
}

export function getBoardStar(boardId: string): Promise<BoardStarState> {
  return get(`/boards/${encodeURIComponent(boardId)}/star`, boardStarStateSchema);
}

export function starBoard(boardId: string): Promise<BoardStarState> {
  return put(`/boards/${encodeURIComponent(boardId)}/star`, boardStarStateSchema);
}

export function unstarBoard(boardId: string): Promise<BoardStarState> {
  return del(`/boards/${encodeURIComponent(boardId)}/star`, boardStarStateSchema);
}

export function reorderBoardStar(
  boardId: string,
  body: ReorderBoardStarBody,
): Promise<BoardStarState> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/star/position`,
    boardStarStateSchema,
    reorderBoardStarBodySchema.parse(body),
  );
}

export function recordBoardRecentView(boardId: string): Promise<BoardRecentView> {
  return put(`/boards/${encodeURIComponent(boardId)}/recent-view`, boardRecentViewSchema);
}

export function useBoardStar(
  boardId: string,
  enabled: boolean,
): UseQueryResult<BoardStarState, ApiError> {
  return useQuery({
    queryKey: queryKeys.boards.star(boardId),
    queryFn: () => getBoardStar(boardId),
    enabled,
  });
}

function invalidateBoardShortcutReads(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: BOARDS_PAGE_PREFIX });
  void queryClient.invalidateQueries({ queryKey: queryKeys.search.jumpTo() });
}

function removeBoardFromShortcuts<T extends BoardsPage | SearchJumpTo>(
  previous: T | undefined,
  boardId: string,
): T | undefined {
  if (previous === undefined) return previous;
  return {
    ...previous,
    starred: previous.starred.filter((board) => board.id !== boardId),
  };
}

function upsertBoardInShortcuts<T extends BoardsPage | SearchJumpTo>(
  previous: T | undefined,
  board: BoardPageBoard | Board,
): T | undefined {
  if (previous === undefined) return previous;
  if (!('background' in board) || !('isTemplate' in board)) return previous;
  return {
    ...previous,
    starred: [board, ...previous.starred.filter((entry) => entry.id !== board.id)],
  };
}

function writeStarState(
  queryClient: QueryClient,
  board: BoardPageBoard | Board,
  starred: boolean,
): void {
  queryClient.setQueryData<BoardStarState>(queryKeys.boards.star(board.id), {
    boardId: board.id,
    starred,
  });

  if (starred) {
    queryClient.setQueriesData<BoardsPage>({ queryKey: BOARDS_PAGE_PREFIX }, (previous) =>
      upsertBoardInShortcuts(previous, board),
    );
    queryClient.setQueryData<SearchJumpTo>(queryKeys.search.jumpTo(), (previous) =>
      upsertBoardInShortcuts(previous, board),
    );
    return;
  }

  queryClient.setQueriesData<BoardsPage>({ queryKey: BOARDS_PAGE_PREFIX }, (previous) =>
    removeBoardFromShortcuts(previous, board.id),
  );
  queryClient.setQueryData<SearchJumpTo>(queryKeys.search.jumpTo(), (previous) =>
    removeBoardFromShortcuts(previous, board.id),
  );
}

function reorderBoardsByNeighbours(
  boards: readonly BoardPageBoard[],
  variables: ReorderStarVariables,
): readonly BoardPageBoard[] {
  const moving = boards.find((board) => board.id === variables.boardId);
  if (moving === undefined) return boards;

  const withoutMoving = boards.filter((board) => board.id !== variables.boardId);
  let insertAt = 0;

  if (variables.previousBoardId !== null) {
    const previousIndex = withoutMoving.findIndex(
      (board) => board.id === variables.previousBoardId,
    );
    if (previousIndex >= 0) {
      insertAt = previousIndex + 1;
    } else if (variables.nextBoardId !== null) {
      const nextIndex = withoutMoving.findIndex(
        (board) => board.id === variables.nextBoardId,
      );
      if (nextIndex < 0) return boards;
      insertAt = nextIndex;
    } else {
      return boards;
    }
  } else if (variables.nextBoardId !== null) {
    const nextIndex = withoutMoving.findIndex(
      (board) => board.id === variables.nextBoardId,
    );
    if (nextIndex < 0) return boards;
    insertAt = nextIndex;
  }

  return [...withoutMoving.slice(0, insertAt), moving, ...withoutMoving.slice(insertAt)];
}

function reorderStarredShortcut<T extends BoardsPage | SearchJumpTo>(
  previous: T | undefined,
  variables: ReorderStarVariables,
): T | undefined {
  if (previous === undefined) return previous;
  const starred = reorderBoardsByNeighbours(previous.starred, variables);
  if (starred === previous.starred) return previous;
  return { ...previous, starred };
}

export function useToggleBoardStar(): UseMutationResult<
  BoardStarState,
  ApiError,
  { board: BoardPageBoard | Board; starred: boolean }
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: ({ board, starred }) =>
      starred ? starBoard(board.id) : unstarBoard(board.id),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: BOARDS_PAGE_PREFIX });
      await queryClient.cancelQueries({ queryKey: queryKeys.search.jumpTo() });
      writeStarState(queryClient, variables.board, variables.starred);
    },
    onSuccess: (state, variables) => {
      writeStarState(queryClient, variables.board, state.starred);
      invalidateBoardShortcutReads(queryClient);
    },
    onError: (_error, variables) => {
      writeStarState(queryClient, variables.board, !variables.starred);
      announce(
        variables.starred
          ? t('primitives.boardStars.starFailed')
          : t('primitives.boardStars.unstarFailed'),
      );
    },
  });
}

export function useReorderBoardStar(): UseMutationResult<
  BoardStarState,
  ApiError,
  ReorderStarVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: ({ boardId, previousBoardId, nextBoardId }) =>
      reorderBoardStar(boardId, { previousBoardId, nextBoardId }),
    onMutate: async (variables): Promise<ReorderStarContext> => {
      await queryClient.cancelQueries({ queryKey: BOARDS_PAGE_PREFIX });
      await queryClient.cancelQueries({ queryKey: queryKeys.search.jumpTo() });

      const boardPages = queryClient.getQueriesData<BoardsPage>({
        queryKey: BOARDS_PAGE_PREFIX,
      });
      const jumpTo = queryClient.getQueryData<SearchJumpTo>(queryKeys.search.jumpTo());

      queryClient.setQueriesData<BoardsPage>(
        { queryKey: BOARDS_PAGE_PREFIX },
        (previous) => reorderStarredShortcut(previous, variables),
      );
      queryClient.setQueryData<SearchJumpTo>(queryKeys.search.jumpTo(), (previous) =>
        reorderStarredShortcut(previous, variables),
      );

      return { boardPages, jumpTo };
    },
    onSuccess: () => {
      invalidateBoardShortcutReads(queryClient);
    },
    onError: (_error, _variables, context) => {
      for (const [key, value] of context?.boardPages ?? []) {
        queryClient.setQueryData(key, value);
      }
      if (context !== undefined) {
        queryClient.setQueryData(queryKeys.search.jumpTo(), context.jumpTo);
      }
      invalidateBoardShortcutReads(queryClient);
      announce(t('primitives.boardStars.reorderFailed'));
    },
  });
}

export function useRecordBoardRecentView(): UseMutationResult<
  BoardRecentView,
  ApiError,
  { boardId: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boardId }) => recordBoardRecentView(boardId),
    onSuccess: () => {
      /*
       * Cancel before invalidating. A read already in flight was sent before
       * this view was recorded — the board switcher's own GET, when a member
       * picks a board before it answers. Its observer has just unmounted, so
       * invalidation alone would not refetch it, and its late success would
       * clear the invalidated flag and keep the old Recent fresh for 30s.
       * Cancelling reverts it; invalidating after (not before: the revert
       * restores the pre-fetch state) marks it stale for the next reader.
       */
      void Promise.all([
        queryClient.cancelQueries({ queryKey: BOARDS_PAGE_PREFIX }),
        queryClient.cancelQueries({ queryKey: queryKeys.search.jumpTo() }),
      ]).then(() => {
        invalidateBoardShortcutReads(queryClient);
      });
    },
  });
}
